import type { PostStatus } from '../graphqlEnums'

import type { SavedPostRef } from './types'

// The shape this reads out of a `createContentItem` success payload. Kept
// structural rather than importing the generated operation type so the mutation's
// selection set can grow without dragging this along. Mirrors the generated
// `ContentItemBody` union, which discriminates on `__typename` — only the
// `PostContent` branch carries `posts`.
type CreatedContent = {
  body?:
    | { __typename?: 'DraftContent' }
    | {
        __typename?: 'PostContent'
        posts: ReadonlyArray<{
          id: string
          channelId: string
          status: PostStatus
          tags: ReadonlyArray<{ id: string }>
        }>
      }
    | null
}

/**
 * The post a `createContentItem` success should link to: the first created
 * variant.
 *
 * "First" is enough because the toast only renders a deep link for a
 * single-variant save (`handleSavedDrafts` gates on `isPlural`) — a fan-out
 * across several channels has no one post to point at.
 *
 * Returns undefined when the payload carries no posts, which happens when the
 * content item holds a channel-less draft (`DraftContent`, so `posts` is absent
 * from the union branch). The composer's per-channel path never creates one, but
 * the type permits it.
 */
export function toSavedPostRef(
  content: CreatedContent,
): SavedPostRef | undefined {
  // Absent `body` is tolerated rather than trusted to the type: this runs inside
  // the success handler, whose catch would report "could not save your post" for
  // a post that did save.
  const { body } = content
  const post = body && 'posts' in body ? body.posts[0] : undefined
  if (!post) return undefined

  return {
    id: post.id,
    channelId: post.channelId,
    status: post.status,
    tagIds: post.tags.map((tag) => tag.id),
  }
}
